import type { LayerModel } from '../core/types';
import { formatCoordinate, parseCoordinate, type GeoCoordinate } from '../domain/coordinates';
import { searchEntities, type EntitySearchResult } from '../domain/entitySearch';
import { icon } from './icons';

const esc = (value: string): string => value.replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char] ?? char);
const BADGE: Readonly<Record<EntitySearchResult['kind'], string>> = { finca: 'F', suerte: 'S', tablon: 'T' };

type SearchItem = { readonly type: 'entity'; readonly result: EntitySearchResult } | { readonly type: 'coordinate'; readonly coordinate: GeoCoordinate };

/**
 * Universal search box over logical entities and WGS84 coordinates ("lat, lon").
 * Enter/click → select + zoom an entity, or go to the coordinate (both delegated).
 */
export class EntitySearchBox {
  private items: readonly SearchItem[] = [];
  private active = 0;
  private timer = 0;

  constructor(
    private readonly input: HTMLInputElement,
    private readonly list: HTMLElement,
    private readonly getModel: () => LayerModel | null,
    private readonly pick: (result: EntitySearchResult) => void,
    private readonly pickCoordinate?: (coordinate: GeoCoordinate) => void,
  ) {
    input.addEventListener('input', () => { window.clearTimeout(this.timer); this.timer = window.setTimeout(() => this.update(), 60); });
    input.addEventListener('focus', () => { if (input.value.trim()) this.update(); });
    input.addEventListener('keydown', (event) => this.onKey(event));
    input.addEventListener('blur', () => window.setTimeout(() => this.close(), 150));
    list.addEventListener('pointerdown', (event) => event.preventDefault());
    list.addEventListener('click', (event) => {
      const button = (event.target as Element).closest<HTMLElement>('[data-search-index]');
      if (button) this.choose(Number(button.dataset.searchIndex));
    });
  }

  update(): void {
    const query = this.input.value.trim();
    if (!query) { this.close(); return; }
    const model = this.getModel();
    const coordinate = this.pickCoordinate ? parseCoordinate(query) : null;
    this.items = [
      ...(coordinate ? [{ type: 'coordinate', coordinate } as const] : []),
      ...searchEntities(model, query).map((result) => ({ type: 'entity', result } as const)),
    ];
    this.active = 0;
    this.list.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.list.innerHTML = this.items.length
      ? this.items.map((item, index) => this.itemMarkup(item, index)).join('')
      : !model
        ? '<div class="search-empty">Carga el Maestro CASUR para buscar Fincas y Suertes, o escribe una coordenada (latitud, longitud).</div>'
        : `<div class="search-empty">Sin resultados para “${esc(query)}”.</div>`;
  }

  close(): void {
    this.list.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  }

  private itemMarkup(item: SearchItem, index: number): string {
    const attributes = `type="button" role="option" class="search-result${index === this.active ? ' active' : ''}" data-search-index="${index}" aria-selected="${index === this.active}"`;
    if (item.type === 'coordinate') return `<button ${attributes}><span class="entity-badge coordinate">${icon('point')}</span><span><strong>Ir a coordenada</strong><small>${formatCoordinate(item.coordinate)} · WGS84 (latitud, longitud)</small></span></button>`;
    const { result } = item;
    return `<button ${attributes}><span class="entity-badge ${result.kind}">${BADGE[result.kind]}</span><span><strong>${esc(result.label)}</strong><small>${esc(result.reason ? `${result.context} · ${result.reason}` : result.context)}</small></span></button>`;
  }

  private choose(index: number): void {
    const item = this.items[index]; if (!item) return;
    this.close();
    this.input.blur();
    if (item.type === 'coordinate') this.pickCoordinate?.(item.coordinate);
    else this.pick(item.result);
  }

  private onKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') { this.close(); this.input.blur(); return; }
    if (!this.items.length) { if (event.key === 'Enter') this.update(); return; }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.active = (this.active + (event.key === 'ArrowDown' ? 1 : -1) + this.items.length) % this.items.length;
      this.list.querySelectorAll<HTMLElement>('[data-search-index]').forEach((item) => { const on = Number(item.dataset.searchIndex) === this.active; item.classList.toggle('active', on); item.setAttribute('aria-selected', String(on)); if (on) item.scrollIntoView({ block: 'nearest' }); });
    } else if (event.key === 'Enter') { event.preventDefault(); this.choose(this.active); }
  }
}
